import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Minimum scroll distance to trigger show/hide
   * @constant {number}
   */
  #scrollThreshold = 10;

  /**
   * Track if header is currently visible
   * @type {boolean}
   */
  #isHeaderVisible = true;

  /**
   * Track if we're at the top of the page
   * @type {boolean}
   */
  #isAtTop = true;

  /**
   * Resize debounce timeout
   * @type {number | null}
   */
  #resizeTimeout = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Handles window resize events
   */
  #handleWindowResize = () => {
    // Debounce resize events
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }

    this.#resizeTimeout = setTimeout(() => {
      // Reset scroll position tracking on resize
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      this.#isAtTop = this.#lastScrollTop <= 100;
      
      // Reset header visibility state on significant width change
      if (this.#isAtTop) {
        this.#resetHeaderToDefaultState();
      }
      
      // Re-initialize intersection observer if needed
      this.#reinitializeIntersectionObserver();
    }, 150);
  };

  /**
   * Reset header to default visible state
   */
  #resetHeaderToDefaultState() {
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    const stickyMode = this.getAttribute('sticky');
    
    if (stickyMode === 'scroll-up') {
      this.removeAttribute('data-animating');
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
      this.#isHeaderVisible = true;
      this.#offscreen = false;
    }
  }

  /**
   * Reinitialize intersection observer if needed
   */
  #reinitializeIntersectionObserver() {
    const stickyMode = this.getAttribute('sticky');
    
    if (stickyMode && this.#intersectionObserver) {
      // Disconnect and recreate observer
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
      this.#observeStickyPosition(stickyMode === 'always');
    }
  }

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting;
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = scrollTop - this.#lastScrollTop;
    
    // For 'always' mode - simple direction tracking
    if (stickyMode === 'always') {
      const headerRect = this.getBoundingClientRect();
      
      if (scrollDelta < 0) {
        // Scrolling up
        if (headerRect.top >= 0) {
          this.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
        }
      } else if (scrollDelta > 0) {
        // Scrolling down
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // For 'scroll-up' mode (both mobile and desktop)
    if (stickyMode === 'scroll-up') {
      // Check if at top of page
      this.#isAtTop = scrollTop <= 100; // 100px threshold for "at top"
      
      // Clear existing timeout
      if (this.#timeout) {
        clearTimeout(this.#timeout);
        this.#timeout = null;
      }

      // If at top of page, always show header
      if (this.#isAtTop) {
        this.removeAttribute('data-animating');
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        this.#isHeaderVisible = true;
        this.#offscreen = false;
        this.#lastScrollTop = scrollTop;
        return;
      }

      // Ignore small scroll movements
      if (Math.abs(scrollDelta) < this.#scrollThreshold) {
        return;
      }

      // Scrolling UP - show header
      if (scrollDelta < 0) {
        this.removeAttribute('data-animating');
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        this.#isHeaderVisible = true;
      } 
      // Scrolling DOWN - hide header (only if it's currently visible)
      else if (scrollDelta > 0 && this.#isHeaderVisible) {
        this.dataset.scrollDirection = 'none';
        this.setAttribute('data-animating', '');
        
        this.#timeout = setTimeout(() => {
          this.dataset.stickyState = 'idle';
          this.removeAttribute('data-animating');
          this.#isHeaderVisible = false;
        }, this.#animationDelay);
      }
      
      this.#lastScrollTop = scrollTop;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Initialize scroll position
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
    this.#isAtTop = this.#lastScrollTop <= 100;

    // Set initial header state
    if (this.#isAtTop) {
      this.#resetHeaderToDefaultState();
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
      }
    }

    // Add resize listener
    window.addEventListener('resize', this.#handleWindowResize.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleWindowResize.bind(this));
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
    }
    
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }
    
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});